/** Contenu pédagogique des règles par jeu. Repris à l'identique du prototype (index.html:1934-1967). */
import type { GameId } from '@ordre-cache/shared';

export interface RuleContent {
  but: string;
  gestes: string[];
  legende: string[];
  astuce: string;
}

export const RULES: Record<GameId, RuleContent> = {
  reseau: {
    but: "Relie tous les tuyaux pour former <b>un seul réseau</b>, sans aucun bout à l'air libre. Quand tout brille en vert, c'est gagné.",
    gestes: [
      "<b>Tape</b> une tuile : elle tourne d'un quart de tour, toujours dans le même sens.",
      'Tape 4 fois : la tuile revient à sa position de départ. Tu peux essayer sans risque.',
      'En défi <b>Marathon</b>, tape le bouton <b>⏭</b> pour passer à la grille suivante.',
    ],
    legende: [
      'Tuyaux de la <b>même couleur</b> → ils sont déjà connectés ensemble.',
      "Plusieurs couleurs → plusieurs réseaux séparés qu'il reste à relier.",
      "Bout de tuyau <b>rouge</b> → une <b>fuite</b> : ce bout n'est branché à rien.",
      'Défis <b>À l\'aveugle</b> et <b>Nuit totale</b> → ni couleurs, ni fuites rouges : seul le compteur global te guide.',
      '« X réseaux séparés · Y fils libres » → ton avancement en direct : vise <b>1</b> réseau et <b>0</b> fil libre.',
      "Tout devient <b>vert brillant</b> → bravo, c'est gagné !",
      'Un compteur <b>x/max</b> → ton budget de rotations (défi Compte-tours) : ne le dépasse pas.',
    ],
    astuce:
      'Commence par éteindre les fuites <b>rouges</b> près des bords : une tuile au bord a peu de positions possibles, et ça débloque le reste. (Sauf en mode Sans frontières, où les bords se rejoignent !)',
  },
  miroirs: {
    but: "Guide le rayon laser jusqu'à son <b>anneau cible</b> au bord de la grille, rebond après rebond — la cible est toujours loin de l'entrée, il faudra construire le chemin.",
    gestes: [
      "<b>Tape</b> un miroir clair : il bascule dans l'autre diagonale (/ devient \\, et inversement). Le rayon change de chemin aussitôt.",
      'Re-tape le même miroir : il revient comme avant. Tu peux tester sans risque.',
      'Les miroirs <b>gris</b> sont soudés : impossible de les bouger — il faut composer avec.',
    ],
    legende: [
      "Point <b>jaune</b> au bord → l'entrée du laser.",
      "<b>Anneau coloré</b> au bord → la cible ; le rayon devient <b>vert</b> quand il l'atteint.",
      'Case <b>vide</b> (petit point) → le rayon la traverse tout droit.',
      'Miroir <b>gris épais</b> → un miroir soudé, non modifiable.',
      "<b>Bloc sombre</b> → un mur : s'il est touché, le rayon est absorbé et tout s'arrête.",
      'Losange <b>violet</b> → une gemme : le rayon doit passer dessus ; elle devient verte une fois traversée.',
      'Point rouge <b>✕</b> → une mine : le rayon ne doit jamais y passer.',
      'Deux rayons <b>orange</b> et <b>bleu</b> → défi Double rayon : chacun doit rejoindre SA propre cible.',
      '« rebonds : actuels/exigés » dans le statut → il faut arriver à la cible avec exactement ce nombre de rebonds.',
    ],
    astuce:
      "Pars de la <b>cible</b> et remonte : par où le rayon doit-il arriver ? Les miroirs soudés et les murs te disent par où le chemin est obligé de passer.",
  },
  vitrail: {
    but: "Trouve quelles cases colorier grâce aux nombres en marge. Quand tout est juste, l'image cachée s'illumine en <b>vitrail</b>.",
    gestes: [
      '<b>Tape</b> une case vide : elle se <b>remplit</b> de couleur.',
      "Re-tape : elle passe en <b>✕</b> (= je suis sûr que c'est vide).",
      'Tape encore : elle redevient vide.',
      "En <b>défi à vies</b>, l'ordre change : vide → ✕ (toujours gratuit) → rempli. Remplir <b>valide</b> la case : si c'est faux, tu perds une vie ♥ et la case reste en ✕.",
    ],
    legende: [
      "Nombres en marge → les <b>blocs</b> de cases pleines de la ligne ou colonne, dans l'ordre. « 2 2 » = 2 pleines, au moins 1 vide, puis 2 pleines.",
      'Cases déjà <b>colorées</b> au départ → un coup de pouce offert aux niveaux Facile et Moyen.',
      'Nombres <b>verts</b> → cette ligne ou colonne est bonne, rien à changer.',
      "<b>✕</b> → une case marquée « sûrement vide » : c'est une aide-mémoire, pas une erreur.",
      '<b>♥</b> → tes vies restantes dans les défis à vies.',
      'Défi <b>Négatif</b> → attention : les nombres comptent les cases <b>vides</b>, pas les pleines !',
    ],
    astuce:
      "Commence par les <b>grands nombres</b> : un bloc presque aussi long que sa ligne n'a que peu de places possibles, donc ses cases du milieu sont sûres.",
  },
  duo: {
    but: 'Remplis toute la grille de <b>soleils</b> et de <b>lunes</b> : jamais 3 pareils à la suite, et autant de soleils que de lunes sur chaque ligne et chaque colonne.',
    gestes: [
      '<b>Tape</b> une case vide : un <b>soleil</b> apparaît.',
      'Re-tape : le soleil devient <b>lune</b>. Tape encore : la case se vide.',
      'Les cases à <b>anneau blanc</b> ne réagissent pas : ce sont des indices fixes.',
      "En <b>défi à vies</b> : change ta case autant que tu veux ; elle est <b>validée</b> quand tu tapes ailleurs. Si c'était faux : -1 vie ♥ et la case se vide.",
    ],
    legende: [
      'Disque <b>doré</b> → un soleil. Croissant <b>violet</b> → une lune.',
      "<b>Anneau blanc</b> autour d'une case → un indice de départ, impossible à modifier.",
      '« 3 identiques alignés » dans le statut → trois soleils (ou trois lunes) se suivent quelque part : interdit.',
      "« trop d'un côté » → une ligne ou colonne n'a pas autant de soleils que de lunes.",
      '« lignes jumelles » → deux lignes (ou colonnes) exactement identiques : interdit dans le défi <b>Sans jumeaux</b>.',
      "<b>♥</b> → tes vies restantes (en défi Éclipse, plus la grille est grande, plus tu as de vies).",
    ],
    astuce:
      'Cherche les <b>paires</b> : deux soleils collés ? Les cases juste avant et après sont forcément des lunes. Et dans un « sandwich » soleil-trou-soleil, le milieu est une lune.',
  },
  lumieres: {
    but: "La grotte est plongée dans le noir. Pose des lampes pour éclairer <b>toutes</b> les cases du sol, sans qu'aucune lampe n'en éblouisse une autre.",
    gestes: [
      '<b>Tape</b> une case du sol vide → tu poses une <b>lampe</b> 💡 (halo doré).',
      '<b>Tape</b> une lampe → elle devient un <b>petit point</b>, ton mémo « pas de lampe ici ».',
      '<b>Tape</b> le petit point → la case redevient vide.',
    ],
    legende: [
      "Case teintée <b>or</b> → elle est éclairée par une lampe (sur sa ligne ou sa colonne, jusqu'au prochain mur).",
      'Lampe <b>rouge</b> → deux lampes se voient sur la même ligne ou colonne : interdit !',
      'Bloc gris avec un <b>chiffre</b> → nombre exact de lampes à coller contre ce mur (haut, bas, gauche, droite).',
      "Chiffre <b>vert</b> → c'est le bon compte. Chiffre <b>rouge</b> → trop de lampes collées.",
      'Petit <b>point</b> → juste un mémo pour toi, ça ne change rien au jeu.',
      '💡 <b>x/K</b> dans le statut → défi spécial : il faut exactement K lampes en tout.',
    ],
    astuce:
      'Commence par les <b>murs chiffrés</b> : si le chiffre est égal au nombre de cases libres autour du mur, tu peux poser ces lampes sans réfléchir. Et mets un petit point là où une lampe est impossible.',
  },
  rotor: {
    but: "Reproduis exactement le <b>motif cible</b> affiché en vignette en haut à droite. Gagné quand plus aucune case n'est orange.",
    gestes: [
      '<b>Tape</b> une case → elle se retourne… et ses <b>voisines</b> se retournent aussi !',
      'La forme du retournement dépend du défi : en <b>croix</b>, en <b>diagonales</b>, ou toute la <b>ligne + colonne</b>.',
      'Re-<b>tape</b> la même case → tout se re-retourne : ça annule ton coup.',
    ],
    legende: [
      'Vignette en haut à droite → le <b>motif cible</b> à copier.',
      'Case surlignée <b>orange</b> → elle ne correspond pas encore à la cible.',
      'Plus aucune case orange → <b>gagné</b> !',
      'Budget de coups affiché → défi <b>Économie</b> : ne dépasse pas ce nombre de taps.',
      '« X lignes · Y colonnes à accorder » → défis <b>Onde</b> et <b>Tempête</b> : repère les lignes entièrement fausses, elles appellent un coup.',
    ],
    astuce:
      "L'<b>ordre</b> des coups ne compte pas, et taper deux fois la même case revient à zéro. Ne cherche donc pas « dans quel ordre » mais « <b>quelles cases</b> » taper : regarde où sont groupées les cases orange.",
  },
  filaments: {
    but: 'Relie chaque rond coloré à son <b>jumeau</b> de la même couleur en tirant un fil. Les fils ne doivent <b>jamais</b> se croiser ni se toucher.',
    gestes: [
      '<b>Tape</b> un rond → tu prends ce fil en main (un <b>anneau blanc</b> apparaît).',
      "<b>Tape</b> une case voisine (haut, bas, gauche ou droite) → le fil <b>avance</b> d'une case.",
      '<b>Tape</b> le rond jumeau quand tu es à côté → le fil est <b>bouclé</b>, bravo !',
      "<b>Tape</b> ton propre fil → il <b>recule</b> jusqu'à cette case (pratique pour corriger).",
    ],
    legende: [
      'Ronds de la même <b>couleur</b> → une paire à relier par un seul fil.',
      '<b>Anneau blanc</b> → le fil que tu tiens en ce moment.',
      '<b>Rocher sombre</b> → case infranchissable : les fils doivent le contourner.',
      'Compteur <b>x/total</b> → défi Tout couvrir : il faut remplir 100% des cases.',
      '<b>Chiffre</b> sur un rond → défi Sur mesure : ce fil doit faire PILE cette longueur.',
      'Chiffre avec <b>≥</b> → défi Grand fleuve : ce fil doit faire AU MOINS cette longueur.',
    ],
    astuce:
      'Commence par les paires proches des <b>bords</b> : fais passer leur fil le long du mur. Un fil qui longe le bord laisse le centre libre pour les autres.',
  },
  ponts: {
    but: "Construis des ponts entre les îles pour que chaque île ait <b>exactement</b> son chiffre de ponts, et que tout l'archipel soit relié en un seul bloc.",
    gestes: [
      '<b>Tape</b> une île → elle est sélectionnée (halo doré).',
      '<b>Tape</b> une île alignée voisine → un <b>pont</b> apparaît entre les deux.',
      'Refais le même geste → pont <b>double</b>. Encore une fois → plus de pont (cycle 0→1→2→0).',
    ],
    legende: [
      "Cercle avec un <b>chiffre</b> → une île, et le nombre total de ponts qu'elle doit recevoir.",
      'Anneau <b>vert</b> → cette île a pile le bon nombre de ponts.',
      'Île <b>rouge</b> → trop de ponts, enlèves-en.',
      'Île « <b>?</b> » → son chiffre est caché, elle doit juste être connectée aux autres.',
      "Compteur de <b>groupes</b> dans le statut → nombre de blocs d'îles séparés : il en faut 1 pour gagner.",
      'Défi <b>Passerelles</b> → ponts doubles interdits (cycle 0→1→0).',
    ],
    astuce:
      "Cherche d'abord les îles <b>obligées</b> : une île « 1 » dans un coin n'a souvent qu'un seul voisin possible, et une île au gros chiffre (comme un 6 avec 3 voisins) doit forcément avoir des ponts doubles partout.",
  },
};
